import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Col, FormGroup, Label } from "reactstrap";
import { GallaeryImage } from "types/campaign/creatives";
import { addCampaignGalleryImage, deleteCampaignGalleryImage } from "services/api/campaign";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "store";
import { updateGallery } from "slices/campaigns/creatives/reducer";
import GalleryIcon from "assets/icons/GalleryIcon";

interface CreativesProps {
    campaignId: string,
    campaignHeroImage: string | null;
    gallery: GallaeryImage[];
    onHeroImageChange: (image: File) => void,
    hideGallery?: boolean
}

const CampaignBanner: React.FC<CreativesProps> = ({ campaignId, gallery, campaignHeroImage, onHeroImageChange, hideGallery = false }) => {
    const selectedAgency = useSelector((state: RootState) => state.auth.selectedAgency);
    const [galleryImages, setGalleryImages] = useState<GallaeryImage[]>(gallery);
    const [heroImage, setHeroImage] = useState<string | null>(campaignHeroImage);

    const [newImages, setNewImages] = useState<File[]>([]);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);

    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        if (selectedFile) {
            onHeroImageChange(selectedFile);
        }
    }, [selectedFile])

    // Set images to local states
    useEffect(() => {
        setGalleryImages(gallery);
        setHeroImage(campaignHeroImage);
    }, [gallery, campaignHeroImage])

    // Open file picker when clicking edit button
    const handleEditClick = () => {
        fileInputRef.current?.click();
    };

    // Handle file selection
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Drag & Drop Handlers for Hero Image
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    // Dropzone Logic for Gallery Images
    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        for (let file of acceptedFiles) {
            const formData = new FormData();
            formData.append('file', file);
            try {
                const response = await addCampaignGalleryImage(selectedAgency?.uuid!, campaignId, formData);
                if (response.success) {
                    const renamedFile = new File([file], response.success, { type: file.type });
                    setNewImages((prev) => [...prev, renamedFile]);
                }
            } catch (error) {
                console.log(error);
            }
        }

    }, []);

    const handleDeleteExisting = async (fileId: string) => {
        try {
            const response = await deleteCampaignGalleryImage(selectedAgency?.uuid!, campaignId, fileId);
            if (response) {
                let updatedFiles = galleryImages.filter((img) => img.uuid !== fileId);
                dispatch(updateGallery({ campaignId, gallery: updatedFiles }));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleDeleteNew = async (file: File) => {
        try {
            const response = await deleteCampaignGalleryImage(selectedAgency?.uuid!, campaignId, file.name);
            if (response) {
                setNewImages((prev) => prev.filter((img) => img.name !== file.name));
            }
        } catch (error) {
            console.log(error);
        }
    };

    // Dropzone Configuration
    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        accept: { ".png, .jpg, .jpeg": [] },
        multiple: true,
    });

    return (
        <React.Fragment>
            {/* Hero Image Section */}
            <div className="form-group">
                <label htmlFor="references" className="fs-14px">Banner Image</label>
                <span className="field-desc">Provide banner image for campaign. Recommended size: 1920x960 px (2:1 aspect ratio).</span>
                <div>
                    <div
                        className={`${heroImage ? 'campaign-creative-img' : 'campaign-creative-img-placeholder'} ${isDragging ? "dragging" : ""}`}
                        style={{
                            backgroundImage: `url(${selectedFile ? URL.createObjectURL(selectedFile) : heroImage})`,
                        }}
                        onClick={handleEditClick}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {heroImage ? (
                            <div className="creative-action">
                                <i className="bx bxs-pencil"></i>
                            </div>
                        ) : (
                            <button type="button" className="creative-add-btn">+</button>
                        )}
                    </div>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".png, .jpg, .jpeg"
                        onChange={handleFileChange}
                    />
                </div>
            </div>
        </React.Fragment>
    );
};

export default CampaignBanner;